import { Box, Flex, Image, Accordion, AccordionItem, AccordionButton, AccordionPanel, useAccordionItemState } from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import logo from 'Asset/image/logo.svg';
import logoSmall from 'Asset/image/logo-small.svg';
import { ChevronDownIcon, ChevronUpIcon, ExpandIcon } from 'Asset/icon';
import SkeletonSidebar from 'Component/skeleton/sidebar';
import { Condition, Element, Iteration } from 'Component/others';
import { TextBold, TextMedium } from 'UiKit/typography';
import { routeList } from 'Route/const';
import { useAppSystem } from 'Store/app';

import { checkIsSelectedChild, checkIsSelectedParent, filterMenuByAccess } from './helpers/utils';
import { onClickParent } from './helpers/event';
import styles from './styles.module.css';

const SidebarAccordionButton = ({ name, children, path, icon, iconActive, isSelectedParent, navigate }) => {
  const { isOpen } = useAccordionItemState();

  return (
    <AccordionButton className={styles['sidebar__container--parent']} onClick={() => onClickParent({ path, navigate, children })}>
      <Flex className={styles['sidebar-parent__container--left']}>
        <Box className={styles['icon__container']}>
          <Element value={icon} />
          <Element value={iconActive} />
        </Box>
        <TextBold>{name}</TextBold>
      </Flex>
      <Condition statement={children?.length > 0}>
        <Condition statement={isOpen} fallback={<ChevronDownIcon />}>
          <ChevronUpIcon />
        </Condition>
      </Condition>

      <Condition statement={isSelectedParent}>
        <Box className={styles['selected-indicator']} />
      </Condition>
    </AccordionButton>
  );
};

const SideBar = ({ isShrink, onExpand, onShrink }) => {
  const accessPermission = localStorage?.getItem('accessPermission');
  const routes = filterMenuByAccess({ routeList, accessPermission });
  const { isLoadingPage } = useAppSystem();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [hoveredRoute, setHoveredRoute] = useState(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isShrink) setHoveredRoute(null);
  }, [isShrink]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const cancelPendingClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleParentEnter = (event, route) => {
    if (!isShrink || route.children?.length === 0) return;
    cancelPendingClose();
    const top = event.currentTarget.getBoundingClientRect().top;
    setHoveredRoute({ name: route.name, path: route.path, children: route.children, top });
  };

  const handleParentLeave = () => {
    cancelPendingClose();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRoute(null);
    }, 100);
  };

  const handlePopoverEnter = () => {
    cancelPendingClose();
  };

  const handlePopoverLeave = () => {
    setHoveredRoute(null);
  };

  return (
    <Box className={`${styles['sidebar__container']} ${isShrink && styles['sidebar__container--shrink']}`}>
      <Box className={styles['sidebar__content']}>
        <Condition statement={!isLoadingPage} fallback={<SkeletonSidebar />}>
          <Flex className={styles['sidebar__head']}>
            <Image src={isShrink ? logoSmall : logo} />
            <ExpandIcon onClick={isShrink ? onExpand : onShrink} />{' '}
          </Flex>

          <Box className={styles['sidebar__body']}>
            <Accordion defaultIndex={[0, 1, 2, 3, 4, 5]} allowMultiple>
              <Iteration
                items={routes}
                render={({ name, children, path, icon, iconActive }) => {
                  const isSelectedParent = checkIsSelectedParent({ path, pathname, children });
                  const route = { name, children, path };

                  return (
                    <AccordionItem
                      className={`${styles['sidebar-item__container']} ${isSelectedParent && styles['sidebar-item__container--actived']}`}
                      key={path}
                      onMouseEnter={(e) => handleParentEnter(e, route)}
                      onMouseLeave={handleParentLeave}
                    >
                      {/* Parent */}

                      <SidebarAccordionButton
                        name={name}
                        children={children}
                        path={path}
                        icon={icon}
                        iconActive={iconActive}
                        isSelectedParent={isSelectedParent}
                        navigate={navigate}
                      />

                      {/* Child expand (hidden via CSS when sidebar is shrunk) */}

                      <Condition statement={children?.length > 0}>
                        <AccordionPanel padding={0}>
                          <Iteration
                            items={children}
                            render={(child) => {
                              const isSelectedChild = checkIsSelectedChild({ pathname, path: child?.path, parentPath: path });

                              return (
                                <Box
                                  className={`${styles['sidebar__container--child']} ${isSelectedChild && styles['sidebar__container--child--actived']}`}
                                  onClick={() => navigate(`${path}/${child?.path}`)}
                                  key={child?.name}
                                >
                                  <Flex className={styles['sidebar-item--child']}>
                                    <TextMedium>{child?.name}</TextMedium>
                                  </Flex>
                                </Box>
                              );
                            }}
                          />
                        </AccordionPanel>
                      </Condition>
                    </AccordionItem>
                  );
                }}
              />
            </Accordion>
          </Box>
        </Condition>
      </Box>

      <Condition statement={isShrink && hoveredRoute}>
        {createPortal(
          <Box
            className={styles['sidebar__container--child-shrink']}
            style={{ top: `${hoveredRoute?.top}px` }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
          >
            <Box className={styles['sidebar__container--child']}>
              <Flex className={styles['sidebar-item--child']}>
                <TextBold>{hoveredRoute?.name}</TextBold>
              </Flex>
            </Box>
            <Iteration
              items={hoveredRoute?.children}
              render={(child) => {
                const isSelectedChild = checkIsSelectedChild({ pathname, path: child?.path, parentPath: hoveredRoute?.path });

                return (
                  <Box
                    className={`${styles['sidebar__container--child']} ${isSelectedChild && styles['sidebar__container--child--actived']}`}
                    onClick={() => navigate(`${hoveredRoute?.path}/${child?.path}`)}
                    key={child?.name}
                  >
                    <Flex className={styles['sidebar-item--child']}>
                      <TextMedium>{child?.name}</TextMedium>
                    </Flex>
                  </Box>
                );
              }}
            />
          </Box>,
          document.body,
        )}
      </Condition>
    </Box>
  );
};

export default SideBar;
